import find from 'lodash/find';
import { matchPath } from 'react-router-dom';
import { compile } from 'path-to-regexp';
import { stringify } from './urlHelpers';
// NOTE: This file imports urlHelpers.js, which may lead to circular dependency
import { buildCategoryIdMap } from './categoryLandingHelpers';

const findRouteByName = (nameToFind, routes) => find(routes, route => route.name === nameToFind);

/**
 * E.g. ```const toListingPath = toPathByRouteName('ListingPage', routes);```
 * Then we can generate listing paths with given params (```toListingPath({ id: uuidX })```)
 */
const toPathByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Path "${nameToFind}" was not found.`);
  }
  return compile(route.path);
};

/**
 * Shorthand for single path call. (```pathByRouteName('ListingPage', routes, { id: uuidX });```)
 */
export const pathByRouteName = (nameToFind, routes, params = {}) => {
  const hasEmptySlug = params && params.hasOwnProperty('slug') && params.slug === '';
  const pathParams = hasEmptySlug ? { ...params, slug: 'no-slug' } : params;
  return toPathByRouteName(nameToFind, routes)(pathParams);
};

/**
 * Find the matching routes and their params for the given pathname
 *
 * @param {String} pathname - Full URL path from root with possible
 * search params and hash included
 *
 * @return {Array<{ route, params }>} - All matches as { route, params } objects if matches has
 * exact flag set to false. If not, an array containing just the first matched exact route is returned.
 */
export const matchPathname = (pathname, routeConfiguration) => {
  const matchedRoutes = routeConfiguration.reduce((matches, route) => {
    const { path, exact = true } = route;
    const match = matchPath(pathname, { path, exact });
    if (match) {
      matches.push({
        route,
        params: match.params || {},
      });
    }
    return matches;
  }, []);

  const matchedExactRoute = matchedRoutes.find(r => {
    return r.exact === true || r.exact == null;
  });

  // We return matched 'exact' path route only if such exists
  // and all matches if no exact flag exists.
  return matchedExactRoute ? [matchedExactRoute] : matchedRoutes;
};

/**
 * ResourceLocatorString is used to direct webapp to correct page.
 * In contrast to Universal Resource Locator (URL), this doesn't contain protocol, host, or port.
 */
export const createResourceLocatorString = (
  routeName,
  routes,
  pathParams = {},
  searchParams = {},
  hash = ''
) => {
  const searchQuery = stringify(searchParams);
  const includeSearchQuery = searchQuery.length > 0 ? `?${searchQuery}` : '';
  const path = pathByRouteName(routeName, routes, pathParams);
  return `${path}${includeSearchQuery}${hash}`;
};

/**
 * Find component related to route name
 * E.g. `const PageComponent = findComponentByRouteName('CheckoutPage', routes);`
 * Then we can call static methods of given component:
 * `dispatch(PageComponent.setInitialValues({ listing, bookingDates }));`
 *
 * @param {String} nameToFind - Route name
 * @param {Array<{ route }>} routes - Route configuration as flat array.
 *
 * @return {Route} - Route that matches the given route name.
 */
export const findRouteByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Component "${nameToFind}" was not found.`);
  }
  return route;
};

// Query params that carry no unique content signal — strip from canonical URLs
const NON_CANONICAL_PARAMS = ['sort', 'page', 'mapSearch', 'origin', 'bounds'];

// Memoized category ID map — static data, safe to cache for the page lifetime
let _cachedIdMap = null;
const getCachedIdMap = () => {
  if (!_cachedIdMap) {
    _cachedIdMap = buildCategoryIdMap();
  }
  return _cachedIdMap;
};

// If the search string represents a pure category filter (no other content-defining params),
// return the canonical category landing page URL. Otherwise return null.
const getCategoryCanonicalFromSearch = searchString => {
  if (!searchString) return null;
  const params = new URLSearchParams(searchString.replace(/^\?/, ''));

  // Find deepest category level param present
  let deepestCategoryId = null;
  for (let level = 4; level >= 1; level--) {
    const val = params.get(`pub_categoryLevel${level}`);
    if (val) {
      deepestCategoryId = val;
      break;
    }
  }

  if (!deepestCategoryId) return null;

  // Remove all category level params and non-canonical params, see if anything remains
  const remaining = new URLSearchParams(params);
  for (let level = 1; level <= 4; level++) {
    remaining.delete(`pub_categoryLevel${level}`);
  }
  NON_CANONICAL_PARAMS.forEach(p => remaining.delete(p));

  // If additional content-defining filters exist, this is unique content — not a duplicate
  if (remaining.toString() !== '') return null;

  const idMap = getCachedIdMap();
  const info = idMap[deepestCategoryId];
  return info ? `/${info.category.url_slug}` : null;
};

/**
 * Get the canonical URL from the given location
 *
 * @param {Array<{ route }>} routes - Route configuration as flat array
 * @param {Object} location - location object from React Router
 *
 * @return {String} Canonical URL of the given location
 *
 */
export const canonicalRoutePath = (routes, location, pathOnly = false) => {
  const { pathname, search, hash } = location;

  const matches = matchPathname(pathname, routes);
  const routeName = matches.length === 1 ? matches[0].route.name : null;

  // ListingPage: keep full path with slug, strip query params
  if (routeName === 'ListingPage') {
    return pathname;
  }

  // CategoryPage: strip all query params — the slug path is the canonical URL
  if (routeName === 'CategoryPage1Seg' || routeName === 'CategoryPage2Seg') {
    return pathname;
  }

  // SearchPage: pure category filter → canonical is the category landing page;
  // other filters → strip sort/page/map params, keep content-defining filters
  if (routeName === 'SearchPage') {
    const categoryCanonical = getCategoryCanonicalFromSearch(search);
    if (categoryCanonical) {
      return categoryCanonical;
    }

    if (search) {
      const params = new URLSearchParams(search.replace(/^\?/, ''));
      NON_CANONICAL_PARAMS.forEach(p => params.delete(p));
      const stripped = params.toString();
      const searchStr = stripped ? `?${stripped}` : '';
      return pathOnly ? `${pathname}${searchStr}` : `${pathname}${searchStr}`;
    }

    return pathname;
  }

  return pathOnly ? pathname : `${pathname}${search}${hash}`;
};
